// app/Pick/PietrePick.jsx
// Slot extra "ottavo posto" per lega Pietre (id_league = 4)
// Stile ispirato al canale YouTube Pietre Ciclismo

import { useEffect, useState } from 'react'
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native'
import { supabase } from '../../lib/supabase'
import { useThemeColors } from '../../lib/useThemeColors'
import { fonts, radius } from '../../lib/theme'
import ModaleSelezione from './ModaleSelezione'
import GradientButton from '../../components/GradientButton'
import { hapticLight, hapticSuccess, hapticError } from '../../lib/haptics'

// ── Brand Pietre ─────────────────────────────────────────────────────
const P = {
  bg:       '#111111',
  bgCard:   '#1A1A1A',
  bgTile:   '#222222',
  border:   '#333333',
  text:     '#FFFFFF',
  muted:    '#888888',
  accent:   '#CCCCCC',
  success:  '#4CAF50',
  error:    '#E53935',
}

// Griglia di "pietre" decorativa
function StoneTexture() {
  const tiles = []
  const cols = 8
  const rows = 2
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const size = 10 + Math.floor((r * cols + c * 3) % 6) * 2
      const opacity = 0.15 + ((r * cols + c * 7) % 5) * 0.06
      tiles.push(
        <View
          key={`${r}-${c}`}
          style={{
            width: size, height: size,
            backgroundColor: '#555',
            borderRadius: 2,
            opacity,
            margin: 2,
          }}
        />
      )
    }
  }
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', paddingVertical: 4 }}>
      {tiles}
    </View>
  )
}

export default function PietrePick({ userId, selGara, selTappa, allRiders, stageStatus }) {
  const tc = useThemeColors()

  const [selected, setSelected]     = useState(null)
  const [saved, setSaved]           = useState(null)
  const [result, setResult]         = useState(null)
  const [modaleOpen, setModaleOpen] = useState(false)
  const [saving, setSaving]         = useState(false)
  const [msg, setMsg]               = useState(null)
  const [loading, setLoading]       = useState(true)

  useEffect(() => {
    if (!userId || !selTappa?.id_stage) return
    setLoading(true)
    setSelected(null); setSaved(null); setResult(null); setMsg(null)

    supabase.rpc('fn_pietre_pick_stage', {
      p_id_user: userId,
      p_id_stage: selTappa.id_stage,
    }).then(({ data }) => {
      if (data?.pick) {
        const rider = allRiders.find(r => r.value === data.pick.id_rider)
        const fallback = { value: data.pick.id_rider, label: data.pick.rider_name }
        setSaved(rider ?? fallback)
        setSelected(rider ?? fallback)
      }
      if (data?.result) setResult(data.result)
      setLoading(false)
    })
  }, [userId, selTappa?.id_stage, allRiders])

  const isLocked = stageStatus === 'live' || stageStatus === 'finished'

  const handleSave = async () => {
    if (!selected) return
    setSaving(true); setMsg(null)
    try {
      const { data, error } = await supabase.rpc('fn_pietre_pick', {
        p_id_user:  userId,
        p_id_stage: selTappa.id_stage,
        p_id_rider: selected.value,
      })
      if (error) throw error
      if (!data?.success) throw new Error(data?.message ?? 'Errore')
      hapticSuccess()
      setSaved(selected)
      setMsg({ type: 'success', text: 'PICK SALVATO' })
    } catch (e) {
      hapticError()
      setMsg({ type: 'error', text: e.message.toUpperCase() })
    } finally { setSaving(false) }
  }

  return (
    <View style={styles.wrapper}>

      {/* Header Pietre */}
      <View style={styles.header}>
        <StoneTexture />
        <View style={styles.headerTextRow}>
          <Text style={styles.headerTitle}>PIETRE</Text>
          <Text style={styles.headerSub}>OTTAVO POSTO</Text>
        </View>
        <View style={styles.headerDivider} />
        <Text style={styles.headerDesc}>
          Indovina il corridore che arriverà 8° · +1 PUNTO
        </Text>
      </View>

      {/* Body */}
      <View style={styles.body}>
        {loading ? (
          <ActivityIndicator color={P.accent} style={{ marginVertical: 16 }} />
        ) : (
          <>
            {/* Risultato */}
            {result && (
              <View style={[styles.resultCard, { borderColor: result.is_correct ? P.success : P.error }]}>
                <Text style={[styles.resultIcon, { color: result.is_correct ? P.success : P.error }]}>
                  {result.is_correct ? '✓' : '✗'}
                </Text>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.resultTitle, { color: result.is_correct ? P.success : P.error }]}>
                    {result.is_correct ? `INDOVINATO · +${result.points} PT` : 'SBAGLIATO · 0 PT'}
                  </Text>
                  <Text style={styles.resultSub}>
                    8° ERA: {(result.actual_name ?? '—').toUpperCase()}
                  </Text>
                  {result.picked_name && (
                    <Text style={styles.resultSub}>
                      TUO PICK: {result.picked_name.toUpperCase()}
                    </Text>
                  )}
                </View>
              </View>
            )}

            {/* Selector — solo se non locked */}
            {!isLocked && (
              <>
                {modaleOpen && (
                  <ModaleSelezione
                    allRiders={allRiders}
                    selected={selected ? [selected] : []}
                    limit={1}
                    onAdd={(r) => { hapticLight(); setSelected(r); setModaleOpen(false); setMsg(null) }}
                    onRemove={() => setSelected(null)}
                    onClose={() => setModaleOpen(false)}
                  />
                )}

                <TouchableOpacity
                  style={[styles.selector, selected && styles.selectorActive]}
                  onPress={() => { hapticLight(); setModaleOpen(true) }}
                >
                  <Text style={styles.selectorNum}>8°</Text>
                  <Text style={[styles.selectorText, !selected && { color: P.muted }]}>
                    {selected ? selected.label.toUpperCase() : 'SCEGLI IL CORRIDORE'}
                  </Text>
                  {selected && (
                    <TouchableOpacity
                      onPress={() => { hapticLight(); setSelected(null); setMsg(null) }}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Text style={styles.removeBtn}>✕</Text>
                    </TouchableOpacity>
                  )}
                </TouchableOpacity>

                {msg && (
                  <Text style={[styles.msg, { color: msg.type === 'success' ? P.success : P.error }]}>
                    {msg.text}
                  </Text>
                )}

                {selected && selected?.value !== saved?.value && (
                  <TouchableOpacity
                    style={[styles.confirmBtn, saving && { opacity: 0.6 }]}
                    onPress={handleSave}
                    disabled={saving}
                  >
                    <Text style={styles.confirmBtnText}>
                      {saving ? 'SALVATAGGIO...' : 'CONFERMA OTTAVO'}
                    </Text>
                  </TouchableOpacity>
                )}

                {saved && selected?.value === saved?.value && !msg && (
                  <Text style={styles.savedLabel}>✓ PICK CONFERMATO</Text>
                )}
              </>
            )}

            {/* Locked — mostra solo pick o messaggio */}
            {isLocked && !result && (
              <View style={styles.lockedRow}>
                <Text style={styles.lockedLabel}>IL TUO PICK:</Text>
                <Text style={[styles.lockedName, !saved && { color: P.muted }]}>
                  {saved ? saved.label.toUpperCase() : 'NESSUN PICK'}
                </Text>
              </View>
            )}
          </>
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    marginTop: 20,
    marginHorizontal: 8,
    borderRadius: 4,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: P.border,
  },

  // ── Header ──────────────────────────────────────────────────────
  header: {
    backgroundColor: P.bg,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 12,
  },
  headerTextRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 10,
    marginTop: 6,
  },
  headerTitle: {
    fontSize: 28,
    fontFamily: fonts.condensedBold,
    color: P.text,
    letterSpacing: 6 },
  headerSub: {
    fontSize: 11,
    fontFamily: fonts.condensedBold,
    color: P.muted,
    letterSpacing: 3,
    textTransform: 'uppercase',
  },
  headerDivider: {
    height: 2,
    backgroundColor: P.text,
    marginVertical: 8,
    width: 40,
  },
  headerDesc: {
    fontSize: 11,
    color: P.muted,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },

  // ── Body ────────────────────────────────────────────────────────
  body: {
    backgroundColor: P.bgCard,
    padding: 14,
  },

  // ── Risultato ───────────────────────────────────────────────────
  resultCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    marginBottom: 12,
    backgroundColor: P.bg,
  },
  resultIcon: {
    fontSize: 24,
    fontFamily: fonts.condensedBold },
  resultTitle: {
    fontSize: 13,
    fontFamily: fonts.condensedBold,
    letterSpacing: 1.5,
    marginBottom: 4,
  },
  resultSub: {
    fontSize: 11,
    color: P.muted,
    letterSpacing: 1,
  },

  // ── Selector ────────────────────────────────────────────────────
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: P.bgTile,
    borderWidth: 1,
    borderColor: P.border,
    borderRadius: 2,
    padding: 12,
    marginBottom: 10,
  },
  selectorActive: {
    borderColor: P.accent,
  },
  selectorNum: {
    fontSize: 18,
    fontFamily: fonts.condensedBold,
    color: P.text,
    letterSpacing: 1,
    minWidth: 30 },
  selectorText: {
    flex: 1,
    fontSize: 13,
    fontFamily: fonts.condensedBold,
    color: P.text,
    letterSpacing: 1.5,
  },
  removeBtn: {
    fontSize: 16,
    color: P.muted,
    paddingHorizontal: 4,
  },

  // ── Confirm ─────────────────────────────────────────────────────
  confirmBtn: {
    backgroundColor: P.text,
    borderRadius: 2,
    padding: 13,
    alignItems: 'center',
    marginTop: 4,
  },
  confirmBtnText: {
    fontSize: 13,
    fontFamily: fonts.condensedBold,
    color: P.bg,
    letterSpacing: 3 },

  // ── Feedback ────────────────────────────────────────────────────
  msg: {
    fontSize: 11,
    fontFamily: fonts.condensedBold,
    letterSpacing: 2,
    textAlign: 'center',
    marginBottom: 8,
  },
  savedLabel: {
    fontSize: 11,
    fontFamily: fonts.condensedBold,
    color: P.success,
    letterSpacing: 2,
    textAlign: 'center',
    marginTop: 6,
  },

  // ── Locked ──────────────────────────────────────────────────────
  lockedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
  },
  lockedLabel: {
    fontSize: 11,
    fontFamily: fonts.condensedBold,
    color: P.muted,
    letterSpacing: 2,
  },
  lockedName: {
    fontSize: 13,
    fontFamily: fonts.condensedBold,
    color: P.text,
    letterSpacing: 1.5,
    flex: 1,
  },
})